<template>
    <div class="bg-teal-dark text-white">
        <div class="lg:flex">
            <div class="bg-gray-200 h-48 lg:h-auto lg:min-h-80 lg:w-80">
                <img
                    v-if="showMainImage"
                    :src="course.main_image.url"
                    :alt="course.title"
                    class="w-full h-full object-cover lg:hidden"
                    @error.native="onMainImgError"/>
                <img
                    v-if="showThumbnailImage"
                    :src="course.thumbnail_image.url"
                    :alt="course.title"
                    class="hidden w-full h-full object-cover lg:block"
                    @error.native="onThumbnailImgError"/>
            </div>
            <div class="flex-1 flex flex-col">
                <div class="ml-auto mb-2 ">
                    <div
                        v-if="courseType"
                        class=" min-w-36 py-1 px-8 text-center float-left"
                        :class="courseTypeBgColor(course.type)">
                        {{ courseType }}
                    </div>
                    <div
                        v-if="course.pathway_name"
                        class="min-w-36 py-1 px-8 text-center float-left"
                        :class="pathwayBgColor(course.pathway_name)">
                        {{ course.pathway_name }}
                    </div>
                    <div class="clear-both"></div>
                </div>
                <div class="flex flex-col h-full pb-6 px-6 sm:px-10" :class="{'pt-6': !course.pathway_name}">
                    <h3 class="text-lg sm:text-2xl font-bold">{{ course.title }}</h3>
                    <ul class="inline-flex divide-x space-x-4 mt-2">
                        <li>{{ course.trainer_name }}</li>
                    </ul>
                    <p class="sm:text-lg mt-4 leading-normal mb-auto">{{ course.summary }}</p>
                    <div class="mt-8 sm:flex sm:flex-row-reverse sm:justify-between sm:items-center">

                        <div
                            class="inline-flex min-w-44 justify-center font-bold text-teal py-2 px-4 rounded-full bg-white border border-teal">
                            <svg class="w-5 h-5 text-pink fill-current" viewBox="0 0 19.744 19.006">
                                <path
                                    d="M16.043 19.006l-6.175-3.9-6.175 3.9L5.5 11.931 0 7.259l7.166-.472 2.7-6.788 2.7 6.788 7.174.472-5.5 4.672z"/>
                            </svg>
                            <div class="inline-block ml-2">
                                {{ course.credits }} Credits
                            </div>
                        </div>
                        <AppLink
                            v-if="showLink"
                            :to="{name: 'course', params: {courseId: course.id}}"
                            theme="primary-button"
                            class="block text-center mt-6 sm:whitespace-nowrap sm:inline-block sm:mt-0">
                            Find out more
                        </AppLink>
                        <Button v-if="hasFeedback" @click.prevent="showFeedback = true"
                                :class="{'invisible': showFeedback}">
                            View training comments
                        </Button>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="showFeedback" class="py-6 px-6 sm:px-10">
            <template v-if="course.multipart">
                <ul class="space-y-5">
                    <template v-for="module in course.modules" :key="module.id">
                        <li v-if="module.feedback">
                            <h3 class="underline font-semibold">{{ module.title }}</h3>
                            <p class="mt-1 whitespace-pre-line">{{ module.feedback || '-' }}</p>
                        </li>
                    </template>
                </ul>
            </template>
            <p v-else class="whitespace-pre-line">{{ course.modules[0].feedback || '-' }}</p>
            <div class="text-center">
                <Button @click.prevent="showFeedback=false" class="mt-6 inline-flex items-center">
                    Close comments
                    <XIcon class="inline-block ml-1 h-5 w-5" aria-hidden="true"/>
                </Button>
            </div>
        </div>
    </div>
</template>

<script>

import AppLink from "@/Components/AppLink";
import Button from "@/Components/Button.vue";
import {XIcon} from '@heroicons/vue/outline';
import {getCourseType} from "@/Lib/Helpers";

export default {

    name: "CoursePanel",

    props: {
        course: {
            type: Object,
            required: true
        },
        showLink: {
            type: Boolean,
            default: () => true
        }
    },

    data() {
        return {
            hasMainImageError: false,
            hasThumbnailImageError: false,
            showFeedback: false
        }
    },

    components: {
        AppLink,
        Button,
        XIcon
    },

    methods: {
        onMainImgError() {
            this.hasMainImageError = true
        },
        onThumbnailImgError() {
            this.hasThumbnailImageError = true
        },
        pathwayBgColor(pathwayName) {
            switch (pathwayName.toUpperCase()) {
                case 'CORE':
                    return 'bg-green-darker';
                case 'CONTENT':
                    return 'bg-green';
                case 'TEAM':
                    return 'bg-teal'
                case 'STRATEGY':
                    return 'bg-pink';
            }

            return ['bg-gray-100', 'text-gray-900'];
        },
        courseTypeBgColor( type ) {
            switch (type) {
                case 1:
                    return 'bg-blue-400';
                case 2:
                    return 'bg-blue-800';
            }

            return ['bg-gray-100', 'text-gray-900'];
        }
    },

    computed: {
        showMainImage() {
            return this.course.main_image && this.course.main_image.url && !this.hasMainImageError;
        },
        showThumbnailImage() {
            return this.course.thumbnail_image && this.course.thumbnail_image.url && !this.hasThumbnailImageError;
        },
        hasFeedback() {
            if (this.course.modules && this.course.modules.length) {
                return this.course.modules.filter(item => item.feedback).length;
            }
        },
        courseType() {
            return getCourseType(this.course.type)
        }
    }
};

</script>
